import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { CheckBoxFormField, NativeSelectFormField, TextAreaFormField } from '@repo/shared-client/components/fields'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm, useWatch } from '@repo/shared-client/form'
import { Link } from '@tanstack/react-router'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'src/lib/intl/react'
import { CREATE_REPORT } from 'src/lib/mutations'
import type {
  CreateReportMutation,
  ReportReasonCatalogQuery,
  ReportReasonCatalogQueryVariables,
  ReportReasonEnum,
  ReportReasonUiGroupEnum,
  ReportTargetKindEnum,
} from 'types/graphql'

const REPORT_REASON_CATALOG_QUERY: TypedDocumentNode<ReportReasonCatalogQuery, ReportReasonCatalogQueryVariables> = gql`
  query ReportReasonCatalogQuery($targetKind: ReportTargetKindEnum!) {
    reportReasonCatalog(targetKind: $targetKind) {
      reason
      defaultTrack
      uiGroup
      showReporterIsDepicted
      showImmediateDanger
      requiresCopyrightDeclaration
      requiresWorkAndLocation
    }
  }
`

type ReportCreateDialogProps = {
  open?: boolean
  onOpenChange?: (details: { open: boolean }) => void
  targetKind: ReportTargetKindEnum
  targetId?: string
  parentTargetKind?: ReportTargetKindEnum
  parentTargetId?: string
  onReportCreated?: (payload: { publicReference: string; caseId: string }) => void
}

type ReportFormValues = {
  reason: string
  details: string
  copyrightDeclaration: boolean
  accuracyDeclaration: boolean
  submitAnonymously: boolean
  reporterIsDepicted: boolean
  immediateDanger: boolean
}

const EMPTY_REPORT_FORM: ReportFormValues = {
  reason: '',
  details: '',
  copyrightDeclaration: false,
  accuracyDeclaration: false,
  submitAnonymously: false,
  reporterIsDepicted: false,
  immediateDanger: false,
}

const GROUP_ORDER: ReportReasonUiGroupEnum[] = ['SAFETY', 'HARM', 'INTEGRITY', 'LISTING', 'OTHER']

const ReportCreate = ({
  targetKind,
  targetId,
  parentTargetKind,
  parentTargetId,
  onReportCreated,
  open,
  onOpenChange,
}: ReportCreateDialogProps) => {
  const { t } = useTranslation()
  const initialRef = React.useRef<HTMLSelectElement | null>(null)
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [success, setSuccess] = useState<{ publicReference: string } | null>(null)
  const formMethods = useForm<ReportFormValues>({
    defaultValues: EMPTY_REPORT_FORM,
  })
  const selectedReason = useWatch({ control: formMethods.control, name: 'reason' }) as ReportReasonEnum | ''
  const reporterIsDepicted = useWatch({ control: formMethods.control, name: 'reporterIsDepicted' })
  const details = useWatch({ control: formMethods.control, name: 'details' })
  const accuracyDeclaration = useWatch({ control: formMethods.control, name: 'accuracyDeclaration' })
  const copyrightDeclaration = useWatch({ control: formMethods.control, name: 'copyrightDeclaration' })

  const catalogQuery = useQuery(REPORT_REASON_CATALOG_QUERY, {
    variables: { targetKind },
    skip: !open,
  })
  const catalog = catalogQuery.data?.reportReasonCatalog ?? []
  const selectedEntry = catalog.find((entry) => entry.reason === selectedReason)

  const titleValue = useMemo(() => {
    switch (targetKind) {
      case 'MEDIA':
      case 'ATTACHED_MEDIA':
        return t('Report Media')
      case 'POST':
        return t('Report Post')
      case 'COMMENT':
        return t('Report Comment')
      case 'MESSAGE':
        return t('Report Message')
      case 'REVIEW':
        return t('Report Review')
      case 'PLACE':
        return t('Report Place Page')
      case 'PUBLICATION':
        return t('Report Publication')
      case 'EVENT':
        return t('Report Event')
      case 'VENDOR_MARKET':
        return t('Report Vendor')
      case 'GROUP':
        return t('Report Group')
      case 'RENDEZVOUS':
        return t('Report Rendezvous')
      default:
        return t('Report Profile')
    }
  }, [t, targetKind])

  const [createReport] = useMutation<CreateReportMutation>(CREATE_REPORT, {
    onCompleted: (data) => {
      setSuccess({ publicReference: data.createReport.publicReference })
      onReportCreated?.(data.createReport)
      setShowSpinnerValue(false)
    },
    onError: (error) => {
      formMethods.setError('details', { type: 'server', message: error.message })
      setShowSpinnerValue(false)
    },
  })

  const groupedOptions = useMemo(() => {
    return GROUP_ORDER.map((group) => ({
      group,
      entries: catalog.filter((entry) => entry.uiGroup === group),
    })).filter((group) => group.entries.length > 0)
  }, [catalog])

  const reasonLabel = (reason: ReportReasonEnum) => {
    switch (reason) {
      case 'CHILD_SEXUAL_ABUSE_MATERIAL':
        return t('Child sexual abuse material')
      case 'APPARENT_MINOR':
        return t('This profile or person appears to be under 18')
      case 'NON_CONSENSUAL_INTIMATE_IMAGERY':
        return t('Non-consensual intimate imagery')
      case 'UNAUTHORIZED_SYNTHETIC_LIKENESS':
        return t('Unauthorized synthetic likeness')
      case 'SEX_TRAFFICKING':
        return t('Sex trafficking or exploitation')
      case 'COMMERCIAL_SEX':
        return t('Commercial sex')
      case 'ANIMAL_ABUSE':
        return t('Animal abuse')
      case 'REAL_DEATH_OR_SNUFF':
        return t('Real death or snuff content')
      case 'OTHER_ILLEGAL':
        return t('Other illegal content')
      case 'PERSONAL_ATTACK_OR_THREAT':
        return t('Personal attack or threat')
      case 'HARASSMENT_OR_BULLYING':
        return t('Harassment or bullying')
      case 'HATE_SPEECH':
        return t('Hate speech')
      case 'STALKING_OR_DOXXING':
        return t('Stalking or doxxing')
      case 'IMPERSONATION':
        return t('Impersonation')
      case 'PERSONAL_INFORMATION':
        return t('Personal information')
      case 'SPAM':
        return t('Spam')
      case 'FAKE_PROFILE':
        return t('Fake profile')
      case 'COPYRIGHT':
        return t('Copyright infringement')
      case 'NOT_OWNER':
        return t('I am the owner / organizer is incorrect')
      case 'REQUEST_OWNER_TRANSFER':
        return t('Request ownership transfer')
      case 'NO_LONGER_EXISTS':
        return t('This listing no longer exists')
      case 'NOT_EVENT':
        return t("This event doesn't exist")
      case 'EVENT_MISREPRESENTATION':
        return t('Event details are misrepresented')
      case 'AUP_LISTING_VIOLATION':
        return t('Listing violates the Acceptable Use Policy')
      case 'NSFW_INCORRECT':
        return t('NSFW rating is incorrect')
      case 'BAN_EVASION':
        return t('Ban evasion')
      default:
        return t('Other')
    }
  }

  const groupLabel = (group: ReportReasonUiGroupEnum) => {
    switch (group) {
      case 'SAFETY':
        return t('Safety')
      case 'HARM':
        return t('Harm')
      case 'INTEGRITY':
        return t('Integrity')
      case 'LISTING':
        return t('Listing')
      default:
        return t('Other')
    }
  }

  const onSubmit = (fieldData: ReportFormValues) => {
    if (!targetId) {
      return
    }
    if (selectedEntry?.requiresCopyrightDeclaration && !fieldData.copyrightDeclaration) {
      formMethods.setError('copyrightDeclaration', {
        type: 'required',
        message: t('You must confirm this declaration to submit a copyright report'),
      })
      return
    }
    if (!fieldData.accuracyDeclaration) {
      formMethods.setError('accuracyDeclaration', {
        type: 'required',
        message: t('You must confirm this report is accurate to the best of your knowledge'),
      })
      return
    }

    setShowSpinnerValue(true)
    createReport({
      variables: {
        input: {
          reason: fieldData.reason as ReportReasonEnum,
          details: fieldData.details,
          targetKind,
          targetId,
          parentTargetKind,
          parentTargetId,
          reporterIsDepicted: fieldData.reporterIsDepicted,
          immediateDanger: fieldData.immediateDanger,
          accuracyDeclaration: fieldData.accuracyDeclaration,
          copyrightDeclaration: fieldData.copyrightDeclaration,
          submitAnonymously: fieldData.reporterIsDepicted ? false : fieldData.submitAnonymously,
        },
      },
    })
  }

  const handleClose = () => {
    formMethods.reset(EMPTY_REPORT_FORM)
    setSuccess(null)
    setShowSpinnerValue(false)
    onOpenChange?.({ open: false })
  }

  useEffect(() => {
    if (!open) {
      formMethods.reset(EMPTY_REPORT_FORM)
      setSuccess(null)
      setShowSpinnerValue(false)
    }
  }, [open, formMethods])

  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => {
        initialRef.current?.focus?.()
      })
      return () => cancelAnimationFrame(id)
    }
  }, [open])

  useEffect(() => {
    if (reporterIsDepicted) {
      formMethods.setValue('submitAnonymously', false)
    }
  }, [reporterIsDepicted, formMethods])

  const showDepicted = Boolean(
    selectedEntry?.showReporterIsDepicted ||
      targetKind === 'MEDIA' ||
      targetKind === 'ATTACHED_MEDIA' ||
      targetKind === 'POST'
  )
  const childSafety = selectedEntry?.defaultTrack === 'CHILD_SAFETY'
  const ncii = selectedEntry?.defaultTrack === 'NCII'
  const copyright = selectedReason === 'COPYRIGHT'
  const canSubmit =
    Boolean(targetId) &&
    Boolean(selectedReason) &&
    Boolean(details?.trim()) &&
    Boolean(accuracyDeclaration) &&
    (!copyright || Boolean(copyrightDeclaration))

  return (
    <Dialog
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose()
          return
        }
        onOpenChange?.({ open: nextOpen })
      }}
      open={open}
    >
      <DialogContent
        className="flex max-h-[90dvh] min-h-0 max-w-lg flex-col overflow-hidden"
        initialFocus={false}
        showCloseButton={!showSpinner}
      >
        {success ? (
          <>
            <DialogHeader className="shrink-0">
              <DialogTitle>{t('Report submitted')}</DialogTitle>
            </DialogHeader>
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-y-contain pb-2">
              <p>{t('Thank you. Keep this reference if you email us about this report.')}</p>
              <p className="rounded-md bg-muted p-3 font-mono text-sm">{success.publicReference}</p>
              <p className="text-muted-foreground text-sm">
                {t('We will not tell the other member who reported them. You can follow up at')}{' '}
                <a className="underline underline-offset-2" href="mailto:safety@spicyfriends.com">
                  safety@spicyfriends.com
                </a>
                {copyright ? (
                  <>
                    {' '}
                    {t('or')}{' '}
                    <a className="underline underline-offset-2" href="mailto:dmca@spicyfriends.com">
                      dmca@spicyfriends.com
                    </a>
                  </>
                ) : null}
                .
              </p>
            </div>
            <DialogFooter className="shrink-0">
              <Button onClick={handleClose} type="button">
                {t('Done')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <Form className="flex min-h-0 flex-1 flex-col gap-4" formMethods={formMethods} onSubmit={onSubmit}>
            <DialogHeader className="shrink-0">
              <DialogTitle>{titleValue}</DialogTitle>
            </DialogHeader>

            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-y-contain pb-2">
              <NativeSelectFormField
                label={t('Report Reason')}
                name="reason"
                placeholder={t('Select a reason...')}
                ref={initialRef}
                required
                validation={{ required: t('Please select a report reason') }}
              >
                {groupedOptions.map((group) => (
                  <optgroup key={group.group} label={groupLabel(group.group)}>
                    {group.entries.map((entry) => (
                      <option key={entry.reason} value={entry.reason}>
                        {reasonLabel(entry.reason)}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </NativeSelectFormField>

              {childSafety ? (
                <Alert>
                  <AlertTitle>{t('Safety reports involving minors')}</AlertTitle>
                  <AlertDescription>
                    {t(
                      'Do not download or forward the material. Also report to Cybertip.ca in Canada, and to NCMEC if the United States is involved. Email safety@spicyfriends.com. This is not a copyright ticket.'
                    )}{' '}
                    <a
                      className="underline underline-offset-2"
                      href="https://www.cybertip.ca"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Cybertip.ca
                    </a>
                    {' · '}
                    <a
                      className="underline underline-offset-2"
                      href="https://report.cybertip.org"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      NCMEC
                    </a>
                  </AlertDescription>
                </Alert>
              ) : null}

              {ncii ? (
                <Alert>
                  <AlertTitle>{t('Non-consensual intimate imagery')}</AlertTitle>
                  <AlertDescription>
                    {t(
                      'We treat these as expedited safety takedowns. You can also email safety@spicyfriends.com. Do not reshare the material.'
                    )}
                  </AlertDescription>
                </Alert>
              ) : null}

              {copyright ? (
                <Alert>
                  <AlertTitle>{t('Copyright notices')}</AlertTitle>
                  <AlertDescription>
                    {t(
                      'This in-app flag is for triage only. For a complete copyright notice, also email dmca@spicyfriends.com with the work, the URL of the material, your contact details, and the statements described on our'
                    )}{' '}
                    <Link className="underline underline-offset-2" to="/helpcenter/legal/copyright-complaints">
                      {t('Copyright Complaints')}
                    </Link>{' '}
                    {t('page.')}
                  </AlertDescription>
                </Alert>
              ) : null}

              {selectedEntry?.showImmediateDanger ? (
                <CheckBoxFormField name="immediateDanger">
                  {t('Someone may be in immediate danger (contact local emergency services; we are not dispatch)')}
                </CheckBoxFormField>
              ) : null}

              {showDepicted ? (
                <CheckBoxFormField name="reporterIsDepicted">
                  {t('I am the person depicted in this content')}
                </CheckBoxFormField>
              ) : null}

              <TextAreaFormField
                label={t('Report Details')}
                name="details"
                placeholder={
                  copyright
                    ? t(
                        'Identify the work, where it appears on Spicy Friends, and how to contact you. Include URLs if you have them.'
                      )
                    : t('Why are you reporting this content...')
                }
                required
                validation={{ required: t('Please describe the issue') }}
              />

              {copyright ? (
                <CheckBoxFormField
                  name="copyrightDeclaration"
                  required
                  rules={{ required: t('You must confirm this declaration to submit a copyright report') }}
                >
                  {t(
                    'I declare under penalty of perjury that I am the copyright owner or authorized to act, that this information is accurate, and that I have a good-faith belief the use is not authorized.'
                  )}
                </CheckBoxFormField>
              ) : null}

              <CheckBoxFormField
                name="accuracyDeclaration"
                required
                rules={{
                  required: t('You must confirm this report is accurate to the best of your knowledge'),
                }}
              >
                {t('I confirm this report is accurate to the best of my knowledge')}
              </CheckBoxFormField>

              <CheckBoxFormField disabled={Boolean(reporterIsDepicted)} name="submitAnonymously">
                {t(
                  'Submit anonymously. The other member is never told who reported; checking this also hides your identity from staff and may limit follow-up.'
                )}
              </CheckBoxFormField>
            </div>

            <DialogFooter className="flex shrink-0 flex-row justify-end gap-2">
              <Button disabled={showSpinner} onClick={handleClose} type="button" variant="outline">
                {t('Cancel')}
              </Button>
              <Button disabled={showSpinner || !canSubmit} type="submit">
                {showSpinner ? <Spinner className="mr-2 size-4" /> : null}
                {t('Report')}
              </Button>
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ReportCreate
